import React from 'react';
import { DollarSign, Radar, Sparkles, Store, TrendingUp, Users } from 'lucide-react';
import type { HeroStat } from '../types';
import { LandingHeroShell } from '../../landing-system';

type Props = {
  trustItems: string[];
  heroStats: HeroStat[];
};

export default function Hero({ trustItems, heroStats }: Props) {
  return (
    <LandingHeroShell
      align="grid"
      innerClassName="grid gap-16 md:grid-cols-[1.05fr_0.95fr] md:items-center"
    >
        <div>
          <div className="mb-6 inline-flex items-center rounded-full border border-[hsl(var(--landing-accent-ring)/0.2)] bg-[hsl(var(--landing-accent-ring)/0.1)] px-4 py-2 text-sm font-medium text-[hsl(var(--landing-accent-foreground))] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
            Trusted Distribution for Brands
          </div>
          <h1 className="max-w-4xl text-5xl font-semibold leading-[0.95] tracking-tight text-white md:text-7xl">
            Most brands don’t need to become louder. They need to be{' '}
            <span className="bg-gradient-to-r from-[hsl(var(--landing-accent-foreground))] via-[hsl(var(--landing-accent-ring))] to-[hsl(var(--landing-accent))] bg-clip-text text-transparent">
              introduced by the right people.
            </span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg leading-8 text-white/70 md:text-xl">
            Kloutsale lets you keep your store, your identity, your pricing, and your customer
            relationship, while trusted creators, experts, and partners help the right customers
            discover what you have already built. Not through interruption. Through recommendation.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="/signup"
              className="rounded-full bg-[hsl(var(--landing-accent))] px-7 py-3.5 text-base font-medium text-white shadow-lg shadow-[hsl(var(--landing-accent)/0.25)] transition hover:bg-[hsl(var(--landing-accent-ring))]"
            >
              Start with Kloutsale
            </a>
            <a
              href="#how-it-works"
              className="rounded-full border border-white/10 px-7 py-3.5 text-base text-white/90 transition hover:bg-white/5"
            >
              See how it works
            </a>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            {trustItems.map((item) => (
              <div
                key={item}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/85 backdrop-blur"
              >
                <span className="h-2 w-2 rounded-full bg-[hsl(var(--landing-accent-ring))]" />
                {item}
              </div>
            ))}
          </div>
          <div className="mt-10 grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3">
            {heroStats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-[22px] border border-white/10 bg-white/5 px-4 py-4 backdrop-blur"
              >
                <div className="text-2xl font-semibold text-white">{stat.value}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-white/55">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="rounded-[34px] border border-white/10 bg-slate-900/95 p-6 text-white shadow-2xl shadow-black/40 backdrop-blur">
            <div className="mb-4 flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-red-400" />
              <div className="h-3 w-3 rounded-full bg-yellow-400" />
              <div className="h-3 w-3 rounded-full bg-green-400" />
            </div>
            <div className="grid gap-4 md:grid-cols-[1.15fr_0.85fr]">
              <div className="rounded-[24px] border border-[hsl(var(--landing-accent-ring)/0.15)] bg-gradient-to-br from-[hsl(var(--landing-accent)/0.25)] via-[hsl(var(--landing-accent-ring)/0.1)] to-[hsl(var(--landing-accent)/0.05)] p-6">
                <div className="text-sm uppercase tracking-[0.18em] text-white/80">
                  Why this model works
                </div>
                <div className="mt-2 text-4xl font-semibold tracking-tight text-white">
                  Trust converts differently than interruption
                </div>
                <div className="mt-3 text-sm leading-6 text-white/85">
                  Ads ask strangers to care quickly. When someone trusted introduces your product,
                  the customer does not start from suspicion. They start from context. That makes
                  the decision easier, the offer clearer, and the sale feel less forced.
                </div>
                <div className="mt-6 grid gap-3">
                  {[
                    { label: 'Pay only when value is actually created', icon: DollarSign },
                    { label: 'Give trusted people a real reason to share', icon: Sparkles },
                    { label: 'Stay present through recommendation, not interruption', icon: Radar },
                  ].map((point) => {
                    const Icon = point.icon;
                    return (
                      <div
                        key={point.label}
                        className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white"
                      >
                        <Icon className="h-4 w-4 text-[hsl(var(--landing-accent-foreground))]" />
                        <span>{point.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-4">
                {[
                  {
                    eyebrow: 'Your store',
                    title: 'Stays your home base',
                    body: 'You keep ownership, fulfillment, identity, and the direct relationship with your customer. Nothing about that changes.',
                    icon: Store,
                  },
                  {
                    eyebrow: 'Katalag + Kloutforce',
                    title: 'Change how discovery happens',
                    body: 'Your products move through trusted people who already have attention and trust, instead of waiting to be found.',
                    icon: Users,
                  },
                  {
                    eyebrow: 'The shift',
                    title: 'You share value when it works',
                    body: 'Instead of spending upfront and hoping something works, value is shared only when a real sale actually happens.',
                    icon: TrendingUp,
                  },
                ].map((card) => {
                  const Icon = card.icon;
                  return (
                    <div
                      key={card.title}
                      className="rounded-[24px] border border-white/10 bg-white/5 p-5"
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 rounded-2xl bg-[hsl(var(--landing-accent-ring)/0.1)] p-2 ring-1 ring-[hsl(var(--landing-accent-ring)/0.2)]">
                          <Icon className="h-4 w-4 text-[hsl(var(--landing-accent-foreground))]" />
                        </div>
                        <div>
                          <div className="text-sm uppercase tracking-[0.18em] text-white/70">
                            {card.eyebrow}
                          </div>
                          <div className="mt-2 text-2xl font-semibold text-white">{card.title}</div>
                          <div className="mt-2 text-sm leading-6 text-white/85">{card.body}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
    </LandingHeroShell>
  );
}
